import React, {forwardRef, useImperativeHandle, useState} from 'react';
import {
  FlexItem,
  FormField,
  FormFieldHelperText,
  FormFieldLabel,
  H2,
  Input,
  StackLayout, Text
} from '@salt-ds/core';

import {
  CheckmarkIcon, ErrorExecuteIcon,
  HiddenSolidIcon,
  VisibleIcon
} from "@salt-ds/icons";
import {FormattedMessage} from "react-intl";
import '../../pages/CommonCard.css';
import Constants from "./Constants";
import {StaticList, StaticListItem, StaticListItemContent} from '@salt-ds/lab';
import {useNavigate} from 'react-router';
import {changePassword} from "../../api/ForgotPasswordApi";
import {setNewSecretPhrase} from "../../api/FirstTimeLogInApi";
import {changeExpiringPassword} from "../../api/ExpiringPasswordChangeApi";
import {forcedChangePassword} from "../../api/ForcedPasswordChangeApi";

const PasswordRequirementItem = ({isEmpty, isValid, text}) => {
  let icon;
  if (isEmpty) {
    icon = <span className="bullet">•</span>;
  } else {
    icon = isValid ?
      <CheckmarkIcon style={{color: 'green'}} /> :
      <ErrorExecuteIcon style={{color: 'red'}} />;
  }

  return (
    <StaticListItem>
      {icon}
      <StaticListItemContent>
        <Text color="inherit">{text}</Text>
      </StaticListItemContent>
    </StaticListItem>
  );
};

function hasConsecutiveSequence(s) {
  for (let i = 0; i < s.length - 2; i++) {
    if (
      s.charCodeAt(i + 1) === s.charCodeAt(i) + 1 &&
      s.charCodeAt(i + 2) === s.charCodeAt(i) + 2
    ) {
      return true;
    }
  }
  return false;
}

const validatePassword = (password) => {
  const lengthRequirement = /^.{8,25}$/.test(password);
  const caseRequirement = /[A-Z]/.test(password) && /[a-z]/.test(password);
  // const specialCharacterRequirement = /^(?:[^!:~_\-{}#$&=@%+\/\\|()\?.”^*;`\[\]'<>,]*[!:~_\-{}#$&=@%+\/\\|()\?.”^*;`\[\]'<>,]){1,3}[^!:~_\-{}#$&=@%+\/\\|()\?.”^*;`\[\]'<>,]*$/.test(password);  /* [] and <> needs to be add*/
  const specialCharacterRequirement = /^(?=(?:.*[!:~_\-{}#$&=@%+\/|()?]){1,3})(?!(?:.*[!:~_\-{}#$&=@%+\/|()?]){4,})[a-zA-Z0-9!:~_\-{}#$&=@%+\/|()?]*$/.test(password);
  const numberRequirement = /[0-9]/.test(password);
  const consecutiveCharacter = !hasConsecutiveSequence(password) && (password !== '') && (password != null)
  const identicalCharacter = /^(?!.*(.)\1\1).+$/.test(password)

  return {
    lengthRequirement,
    caseRequirement,
    specialCharacterRequirement,
    numberRequirement,
    consecutiveCharacter,
    identicalCharacter
  };
};

const isPasswordValid = (password, setError, setErrorMessage, setPassword) => {
  const passwordValidation = validatePassword(password);
  if (password == null || password === '') {
    setError(prevState => ({ ...prevState, requiredFieldError: true }));
    setErrorMessage(Constants.requiredFieldError);
    return false;
  } else if (!(
    passwordValidation.lengthRequirement &&
    passwordValidation.caseRequirement &&
    passwordValidation.specialCharacterRequirement &&
    passwordValidation.numberRequirement &&
    passwordValidation.consecutiveCharacter &&
    passwordValidation.identicalCharacter
  )) {
    setError(prevState => ({ ...prevState, validatePasswordError: true }));
    setErrorMessage(Constants.passwordRequirementError);
    setPassword("");
    return false;
  }
  return true;
};

function validCurrentPassword(currentPassword, setCurrentPasswordError, setCurrentPasswordErrorMessage) {
  // if current password is empty
  if (currentPassword === null || currentPassword === '' || currentPassword.length === 0) {
    setCurrentPasswordError(true);
    setCurrentPasswordErrorMessage("This field is required");
    return false;
  } else {
    setCurrentPasswordError(false);
    setCurrentPasswordErrorMessage("");
  }
  //TODO: write logic to check current password is valid or not
  return true;
}

async function handleForgotPasswordFlow(userId, password, accessResourceToken, setLoadingState, navigate, props, setError, setErrorMessage, setPassword) {
  const response = await changePassword(userId, password, accessResourceToken);
  if (response.id === "success") {
    const { passwordExpiryDate, daysUntilPasswordExpires } = response;
    sessionStorage.setItem('orgName', response.orgNumber);
    setLoadingState(false);
    navigate(props.redirectUrl, {
      state: {
        daysUntilPasswordExpires,
        passwordExpiryDate,
        from: Constants.forgotPasswordComplete
      }
    });
  } else {
    handleErrorResponse(response, setError, setErrorMessage, setPassword, setLoadingState, props);
  }
}

async function handleFirstTimeUserFlow(orgName, loginName, password, setLoadingState, navigate, props) {
  const deviceSignature = sessionStorage.getItem('deviceSignature');
  const response = await setNewSecretPhrase(orgName, loginName, password, deviceSignature);
  if (response.data && response.data.isPasswordChanged === "SUCCESS") {
    setLoadingState(false);
    navigate(props.redirectUrl, {
      state: {
        from: '/firstTimeLoginSetup',
        passwordExpiryDate: response.data.passwordExpiryDate
      }
    });
  } else if (response.error && response.error.detail) {
    props.showErrorOnPage(response.error.detail);
    setLoadingState(false);
  }
}

async function handleExpiringChangePasswordFlow(orgName, loginName, currentPassword, password, props, setLoadingState, setError, setErrorMessage, setPassword) {
  try {
    const response = await changeExpiringPassword(orgName, loginName, currentPassword, password);
    if (response.data) {
      props.showSuccessOnPage((response.data.passwordExpiryDate === null || response.data.passwordExpiryDate === "" || response.data.daysUntilPasswordExpires === 0)
        ? Constants.passwordChangeSuccessMessage
        : Constants.passwordChangeSuccessExpireAfterDays(response.data.daysUntilPasswordExpires, response.data.passwordExpiryDate));
      props.setPasswordChangeSuccess(true);
      setLoadingState(false);
    } else {
      handleErrorResponse(response, setError, setErrorMessage, setPassword, setLoadingState, props);
    }
  } catch (error) {
    console.error('Error changing password : ', error);
    props.showErrorOnPage(error);
    setLoadingState(false);
  }
}

async function handleForceChangePasswordFlow(orgName, loginName, password, props, setLoadingState, setError, setErrorMessage, setPassword) {
  try {
    const response = await forcedChangePassword(orgName, loginName, password);
    if (response.data) {
      props.showSuccessOnPage((response.data.passwordExpiryDate === null || response.data.passwordExpiryDate === "" || response.data.daysUntilPasswordExpires === 0)
        ? Constants.passwordChangeSuccessMessage
        : Constants.passwordChangeSuccessExpireAfterDays(response.data.daysUntilPasswordExpires, response.data.passwordExpiryDate));
      props.setPasswordChangeSuccess(true);
      setLoadingState(false);
    } else {
      handleErrorResponse(response, setError, setErrorMessage, setPassword, setLoadingState, props);
    }
  } catch (error) {
    console.error('Error changing password : ', error);
    props.showErrorOnPage(error);
    setLoadingState(false);
  }
}

function handleErrorResponse(response, setError, setErrorMessage, setPassword, setLoadingState, props) {
  if (response.error.code === "FIELD_VALIDATION_ERROR") {
    if(props.isExpiringChangePasswordFlow) {
      props.setCurrentPasswordError(true);
      props.setCurrentPasswordErrorMessage(Constants.expiringCurrentPasswordIncorrectError);
    }
    else {
      setError(prevState => ({ ...prevState, orgOrUserIdError: true }));
      setErrorMessage(Constants.orgOrUserIdError);
    }
  } else if (response.error.code === "BAD_DATA") {
    setError(prevState => ({ ...prevState, previousTenPasswordsError: true }));
    setErrorMessage(Constants.previousTenPasswordsError);
    setPassword("");
  } else if (response.error && response.error.detail) {
    setPassword("");
    props.showErrorOnPage(response.error.detail);
  }
  setLoadingState(false);
}

async function handleFlowSubmit(flowProps, props, password, setLoadingState, navigate, setError, setErrorMessage, setPassword) {
  const { userId, orgName, loginName, accessResourceToken } = flowProps;
  if (props.isForgotPasswordFlow) {
    await handleForgotPasswordFlow(userId, password, accessResourceToken, setLoadingState, navigate, props, setError, setErrorMessage, setPassword);
  } else if (props.isFirstTimeUserFlow) {
    await handleFirstTimeUserFlow(orgName, loginName, password, setLoadingState, navigate, props);
  } else if (props.isExpiringChangePasswordFlow) {
    await handleExpiringChangePasswordFlow(orgName, loginName, props.currentPassword, password, props, setLoadingState, setError, setErrorMessage, setPassword);
  } else if (props.isForceChangePasswordFlow) {
    await handleForceChangePasswordFlow(orgName, loginName, password, props, setLoadingState, setError, setErrorMessage, setPassword);
  }
}

const ChangePasswordComponent = forwardRef((props, ref) => {
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState({
      requiredFieldError: false,
      orgOrUserIdError: false,
      previousTenPasswordsError: false,
      validatePasswordError: false
    });
    const [errorMessage, setErrorMessage] = useState("");
    const navigate = useNavigate();

    const setLoadingState = (isLoading) => {
      if (props.setIsLoading) props.setIsLoading(isLoading);
    };

    const handlePasswordChange = (event) => {
      setPassword(event.target.value);
      if (event.target.name === 'password') {
        setError((prevErrors) => ({
          ...prevErrors,
          requiredFieldError: false,
          validatePasswordError: false,
          orgOrUserIdError: false,
          previousTenPasswordsError: false
        }));
        setErrorMessage("");
      }
    };

    const handleSubmit = async (event) => {

      event?.preventDefault();

      const accessResourceToken = sessionStorage.getItem('accessResourceToken');
      const userId = sessionStorage.getItem('userId');
      const loginName = sessionStorage.getItem('loginName');
      const orgName = sessionStorage.getItem('orgName');

      setLoadingState(true);

      if (props.isExpiringChangePasswordFlow && !validCurrentPassword(props.currentPassword, props.setCurrentPasswordError, props.setCurrentPasswordErrorMessage)) {
        setLoadingState(false);
        return;
      }

      if (!isPasswordValid(password, setError, setErrorMessage, setPassword)) {
        setLoadingState(false);
        return;
      }

      await handleFlowSubmit({ userId, orgName, loginName, accessResourceToken }, props, password, setLoadingState, navigate, setError, setErrorMessage, setPassword);
    };

    useImperativeHandle(ref, () => ({
      submitForm: handleSubmit,
    }));

    const validations = validatePassword(password);
    const isEmpty = password.length === 0;
    const hasError = error.requiredFieldError || error.orgOrUserIdError ||
      error.previousTenPasswordsError || error.validatePasswordError;

    const togglePasswordVisibility = () => {
      setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    return (
      <form autoComplete="off" data-testid="change-password-form-submit" onSubmit={handleSubmit}>
        <fieldset disabled={props.isLoading || props.isDisabled} style={{border:0, padding:0, margin:0}}>
          <StackLayout gap={1}>
            <FlexItem className={"form-layout"}>
              <FormField validationStatus={hasError ? "error" : undefined}>
                <FormFieldLabel>New Password</FormFieldLabel>
                <Input
                  data-testid="password-input"
                  id="password"
                  inputProps={{
                    ...(showPassword ? {type: "text"} : {type: "password"}),
                    name: 'password',
                    value: password,
                    disabled: props.isLoading || props.isDisabled,
                    maxLength: "25",
                    "aria-required": "true",
                    "aria-describedby": hasError ? "password-error" : undefined
                  }}
                  onChange={handlePasswordChange}
                  endAdornment={
                    showPassword
                      ? <VisibleIcon tabIndex={0} onKeyPress={togglePasswordVisibility} onClick={togglePasswordVisibility} disabled={props.isLoading || props.isDisabled}
                        role="button"
                        aria-label="Hide password"
                      />
                      : <HiddenSolidIcon tabIndex={0} onKeyPress={togglePasswordVisibility} onClick={togglePasswordVisibility} disabled={props.isLoading || props.isDisabled}
                        role="button"
                        aria-label="Show password"
                      />
                  }
                />
                {!hasError && <FormFieldHelperText id="password-error">{Constants.passwordHelper}</FormFieldHelperText>}
                <FormFieldHelperText id="password-error" role="alert">{errorMessage}</FormFieldHelperText>
              </FormField>
            </FlexItem>

            <FlexItem>
              <H2><FormattedMessage id="logon.ui.password.requirements.title" /></H2>
              <StaticList>
                <PasswordRequirementItem
                  isEmpty={isEmpty}
                  isValid={validations.lengthRequirement}
                  text={Constants.passwordRequirementsItem1}
                />
                <PasswordRequirementItem
                  isEmpty={isEmpty}
                  isValid={validations.caseRequirement}
                  text={Constants.passwordRequirementsItem2}
                />
                <PasswordRequirementItem
                  isEmpty={isEmpty}
                  isValid={validations.numberRequirement}
                  text={Constants.passwordRequirementsItem3}
                />
                <PasswordRequirementItem
                  isEmpty={isEmpty}
                  isValid={validations.specialCharacterRequirement}
                  text={Constants.passwordRequirementsItem4}
                />
                <PasswordRequirementItem
                  isEmpty={isEmpty}
                  isValid={validations.consecutiveCharacter}
                  text={Constants.passwordRequirementsItem5}
                />
                <PasswordRequirementItem
                  isEmpty={isEmpty}
                  isValid={validations.identicalCharacter}
                  text={Constants.passwordRequirementsItem6}
                />
              </StaticList>
            </FlexItem>

            <FlexItem>
              <H2><FormattedMessage id="logon.ui.password.restrictions.title" /></H2>
              <StaticList>
                <StaticListItem>
                  <span className="bullet">•</span>
                  <StaticListItemContent>
                    <Text color="inherit">{Constants.passwordRestrictionsItem1}</Text>
                  </StaticListItemContent>
                </StaticListItem>
                <StaticListItem>
                  <span className="bullet">•</span>
                  <StaticListItemContent>
                    <Text color="inherit">{Constants.passwordRestrictionsItem2}</Text>
                  </StaticListItemContent>
                </StaticListItem>
              </StaticList>
            </FlexItem>
          </StackLayout>
        </fieldset>
      </form>
    );
  }
);

export default ChangePasswordComponent;
